import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import InsuranceEmptyState from './steps/empty-state';
import InsuranceList from './steps/list';
import { InsuranceFindTypes } from './constants';
import { CustomersController } from '@/shared/endpoints/customers';
import { CustomersStepTypeEnum } from '@/shared/endpoints/customers/models/enums';
import { ABSOLUTE_ROUTES } from '@/shared/constants/routes';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { stepPayloadHandler } from '../../helpers';

import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import { filterInsurances } from '@/shared/helpers/insurance';
import { usePostHog } from '@/shared/hooks/usePostHog';

const Insurance = () => {
    const user = useAppSelector(selectOnboardingUser)!;
    const sendDataToGTM = useGTMDispatch();
    const posthog = usePostHog();
    const [findType, setFindType] = useState<InsuranceFindTypes>();
    const filteredInsurances = useMemo(() => filterInsurances(user.insurances), [user.insurances]);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const onLookup = () => setFindType('LOOKUP');

    const onGoBack = () => {
        navigate(ABSOLUTE_ROUTES.ONBOARDING.ABOUT_YOU.EMERGENCY_CONTACT);
    }

    const onContinue = useCallback(async () => {
        if (!user.userOnboarding?.details) return;

        const payload = _.cloneDeep(user.userOnboarding);
        payload.details = {
            ...payload.details!,
            ...stepPayloadHandler(user?.userOnboarding?.details?.currentStep, CustomersStepTypeEnum.Prescriptions)
        };

        await dispatch(CustomersController.updateMetadata(payload));
        sendDataToGTM({
            event: GtmEvent.INSURANCE_ADDED,
            userId: user?.userProfile?.accountId,
            count: user && filteredInsurances?.length + user.insuranceUploads?.length
        });
        posthog?.capture(GtmEvent.INSURANCE_ADDED);
        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.MEDBOX_HUE);
    }, [user, dispatch, sendDataToGTM, filteredInsurances?.length, posthog, navigate]);

    if (user && (filteredInsurances?.length || user.insuranceUploads?.length))
        return (
            <div className="lg-max:px-[1rem]">
                <InsuranceList onGoBack={onGoBack} findType={findType} onContinue={onContinue} />
            </div>
        );

    return (
        <div className="lg-max:px-[1rem]">
            <InsuranceEmptyState onGoBack={onGoBack} onLookup={onLookup} />
        </div>
    );
};

export default Insurance;
